/**
 * AdaptiveBulkMarkerLayer — renders bulk-action stop markers using WebGL
 * (deck.gl IconLayer + TextLayer) when supported, falling back to DOM-based
 * AdvancedMarker components when WebGL is unavailable.
 *
 * Both rendering paths resolve colors through the same `resolveMarkerColors`
 * utility so marker appearance is identical regardless of renderer.
 */
import { useMap } from '@vis.gl/react-google-maps'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'

import { useMarkerDensity } from 'components/common/map/webgl/useMarkerDensity'
import { useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import WebGLMarkerSurface from 'components/common/map/webgl/WebGLMarkerSurface'

import BulkDeckMarkerLayer from './BulkDeckMarkerLayer'
import StopMarker from './StopMarker'

const STATEFUL_ICONS = ['to-be-assigned-marker', 'unassigned-marker']

const getFeatureId = feature => get(feature, 'properties.id')
const getFeaturePosition = feature => get(feature, 'geometry.coordinates')

const AdaptiveBulkMarkerLayer = ({
  features = [],
  totalStopCount = 0,
  onClick,
  onHover,
  onMouseEnter,
  onMouseLeave,
  labelDensityLevel = 6,
  highlightedStopIds = null,
}) => {
  const webglSupported = useWebGLSupport()
  const map = useMap()

  // Zoom only, deliberately not bounds — culling must not change while panning,
  // or every DOM marker would remount on each drag.
  const [zoom, setZoom] = useState(() => map?.getZoom?.())
  useEffect(() => {
    if (!map) return undefined
    const syncZoom = () => setZoom(map.getZoom?.())
    syncZoom()
    const zoomListener = map.addListener('zoom_changed', syncZoom)
    return () => zoomListener.remove()
  }, [map])

  const keepIds = useMemo(() => {
    const ids = features
      .filter(feature => {
        const { icon, id, configuredServiceEventId, _isStagedForMove: isStaged } = get(feature, 'properties') || {}
        if (STATEFUL_ICONS.includes(icon) || isStaged === true) return true
        return !!highlightedStopIds && (highlightedStopIds.has(id) || highlightedStopIds.has(configuredServiceEventId))
      })
      .map(getFeatureId)
    return new Set(ids)
  }, [features, highlightedStopIds])

  const visibleFeatures = useMarkerDensity({
    items: features,
    zoom,
    keepIds,
    getId: getFeatureId,
    getPosition: getFeaturePosition,
  })

  const domMarkers = (
    <>
      {visibleFeatures.map(feature => {
        const properties = get(feature, 'properties') || {}
        // Match on either key — daily maps key highlightedStopIds by `id`
        // (workOrderId), master by configuredServiceEventId.
        const haloColor = highlightedStopIds
          ? highlightedStopIds.get(properties.id) ?? highlightedStopIds.get(properties.configuredServiceEventId) ?? null
          : null
        return (
          <StopMarker
            key={get(feature, 'properties.id')}
            feature={feature}
            haloColor={haloColor}
            totalStopCount={totalStopCount}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        )
      })}
    </>
  )

  return (
    <WebGLMarkerSurface componentName="AdaptiveBulkMarkerLayer" enabled={webglSupported} fallback={domMarkers}>
      <BulkDeckMarkerLayer
        features={features}
        highlightedStopIds={highlightedStopIds}
        labelDensityLevel={labelDensityLevel}
        totalStopCount={totalStopCount}
        onClick={onClick}
        onHover={onHover}
      />
    </WebGLMarkerSurface>
  )
}

AdaptiveBulkMarkerLayer.propTypes = {
  features: PropTypes.array,
  totalStopCount: PropTypes.number,
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  labelDensityLevel: PropTypes.number,
  highlightedStopIds: PropTypes.instanceOf(Map),
}

export default React.memo(AdaptiveBulkMarkerLayer)
